import { type ComponentType, type LazyExoticComponent, lazy } from 'react';

/**
 * Auto-recovery for stale code-split chunks after a deploy.
 *
 * Most routes are lazy-loaded (see src/components/Router.jsx). After a deploy the
 * hashed chunk filenames change and the old files are removed from hosting, so a
 * tab still running an older release throws "Importing a module script failed."
 * (Safari) / "Failed to fetch dynamically imported module" (Chrome) when it tries
 * to navigate to a not-yet-loaded route.
 *
 * Recovery is a single reload to pull the fresh manifest. A short cooldown stored
 * in sessionStorage prevents reload loops: if a chunk error recurs within the
 * cooldown (the reload did not fix it) we stop and let the ErrorBoundary surface
 * the failure. sessionStorage (not localStorage) is used so the guard survives the
 * appVersion localStorage.clear() in src/index.jsx and stays scoped per tab. When
 * sessionStorage is unavailable (e.g. private mode) the guard falls back to
 * globalThis.name, which also survives a same-tab reload, so we still avoid loops.
 */

const GUARD_KEY = 'chunkReloadGuard';
const NAME_GUARD_PREFIX = 'chunkReloadGuard=';
const COOLDOWN_MS = 15000;

let listenerRegistered = false;

// `globalThis.name` uniquely persists across a same-tab reload, so it backs the
// storage-blocked fallback guard. TypeScript declares `name` on the Window interface
// but not on `typeof globalThis`, so read and write it through this typed view.
const globalWithName = globalThis as typeof globalThis & { name: string };

/**
 * Read the last chunk-error reload timestamp. Primary store is sessionStorage; we
 * fall back to globalThis.name (which survives same-tab reloads) whenever
 * sessionStorage yields no value. That covers both fully-blocked storage (getItem
 * throws) and the asymmetric case — classic Safari private mode, where getItem
 * returns null but setItem throws, so writeGuardTimestamp had to stash the timestamp
 * in globalThis.name. Reading it only when getItem throws would miss that stash and
 * defeat the loop guard. Returns null when neither store can be used (storage
 * unusable AND globalThis.name already in use by something else), signalling we
 * cannot guard a reload.
 */
function readGuardTimestamp(): number | null {
    let storageUsable = true;
    let stored: string | null = null;
    try {
        stored = sessionStorage.getItem(GUARD_KEY);
    } catch {
        // sessionStorage blocked (getItem throws) — fall back to globalThis.name.
        storageUsable = false;
    }
    if (stored) return Number(stored) || 0;
    const name = globalWithName.name || '';
    if (name.startsWith(NAME_GUARD_PREFIX)) {
        return Number(name.slice(NAME_GUARD_PREFIX.length)) || 0;
    }
    // No guard recorded yet, so a reload is safe as long as we have somewhere to
    // record it: sessionStorage (storageUsable) or a free globalThis.name (name === '').
    // Only when storage is unusable AND globalThis.name is held by something else is
    // there nowhere to persist the guard — return null so we skip the reload and avoid
    // an unguarded loop.
    if (storageUsable || name === '') return 0;
    return null;
}

/**
 * Persist the reload timestamp. Falls back to globalThis.name when sessionStorage is
 * unavailable, but never clobbers a globalThis.name already in use. Returns false
 * when it could not be persisted anywhere, so the caller can skip an unguarded
 * reload rather than risk an infinite loop.
 */
function writeGuardTimestamp(timestamp: number): boolean {
    try {
        sessionStorage.setItem(GUARD_KEY, String(timestamp));
        return true;
    } catch {
        const name = globalWithName.name || '';
        if (name === '' || name.startsWith(NAME_GUARD_PREFIX)) {
            globalWithName.name = NAME_GUARD_PREFIX + timestamp;
            return true;
        }
        return false;
    }
}

function recentlyReloaded(): boolean {
    const last = readGuardTimestamp();
    // null = no usable guard store; treat as "already reloaded" so we never loop.
    if (last === null) return true;
    return Date.now() - last < COOLDOWN_MS;
}

/**
 * Reload once to recover from a stale-chunk import failure. Returns true if a
 * reload was triggered, false if the guard blocked it (cooldown still active, or
 * no usable guard store in which to record the reload).
 */
export function reloadOnceForChunkError(): boolean {
    if (recentlyReloaded()) return false;
    if (!writeGuardTimestamp(Date.now())) return false;
    globalThis.location.reload();
    return true;
}

// Messages browsers use when a dynamically imported chunk cannot be fetched or
// parsed (the stale-chunk case). Wording differs across engines.
const CHUNK_LOAD_ERROR =
    /Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module/i;

function isChunkLoadError(error: unknown): boolean {
    return error instanceof Error && CHUNK_LOAD_ERROR.test(error.message);
}

/**
 * Wrap a dynamic-import factory so a stale-chunk failure triggers a guarded
 * reload. Exported for unit testing; production code uses lazyWithRetry.
 */
export function withChunkRetry<T>(factory: () => Promise<T>): () => Promise<T> {
    return () =>
        factory().then(
            (module) => {
                // When the vite:preloadError listener schedules a reload it calls
                // preventDefault(), which makes Vite's __vitePreload resolve with
                // `undefined` instead of rejecting. A reload is already in flight, so
                // stay suspended rather than let React.lazy dereference undefined.default.
                if (module == null && recentlyReloaded()) {
                    return new Promise<T>(() => {});
                }
                return module;
            },
            (error: unknown) => {
                // Only reload for an actual stale-chunk failure; a genuine runtime error
                // during module init must surface immediately, not be masked by a reload.
                // The vite:preloadError listener remains the catch-all for fetch failures.
                if (isChunkLoadError(error) && reloadOnceForChunkError()) {
                    // Reload scheduled — keep the caller suspended until navigation.
                    return new Promise<T>(() => {});
                }
                throw error;
            }
        );
}

export function registerChunkErrorHandler(): void {
    if (listenerRegistered) return;
    listenerRegistered = true;
    globalThis.addEventListener('vite:preloadError', (event: Event) => {
        // Only suppress Vite's default throw when we actually reload; otherwise let
        // the error surface (to Sentry / the ErrorBoundary) instead of being silenced.
        if (reloadOnceForChunkError()) {
            event.preventDefault();
        }
    });
}

/**
 * Drop-in replacement for React.lazy that recovers from stale-chunk failures.
 * On import rejection it reloads once (guarded); if the guard blocks the reload
 * the error is re-thrown so the ErrorBoundary can render the fallback.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
    factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
    return lazy(withChunkRetry(factory));
}
